import { TFunction } from 'i18next'
import { InitiateThreeDSErrorCode, RecoverableErrorCode, TopazStatus } from '../../model'

type ErrorMessageSelector = (t: TFunction) => string

const unknownErrorMessage: ErrorMessageSelector = (t) => t('paymentMethod:errors.unknown')

const threeDSErrorMessageSelectors: Record<InitiateThreeDSErrorCode, ErrorMessageSelector> = {
  [InitiateThreeDSErrorCode.PAYMENT_REQUIRED]: (t) => t('paymentMethod:threeDS.errors.paymentRequired'),
  [InitiateThreeDSErrorCode.PRECONDITION_FAILED]: (t) => t('paymentMethod:threeDS.errors.preConditionFailed'),
  [InitiateThreeDSErrorCode.INTERNAL_SERVER_ERROR]: unknownErrorMessage,
}

const topazErrorMessageSelectors: Partial<Record<TopazStatus, ErrorMessageSelector>> = {
  [TopazStatus.InvalidDetails]: (t) => t('paymentMethod:errors.default.cardNumber'),
  [TopazStatus.DuplicateCard]: (t) => t('paymentMethod:errors.duplicateCard'),
  [TopazStatus.UnacceptableCard]: (t) => t('paymentMethod:errors.unsupportedCard'),
  [TopazStatus.UnacceptableCardIssuerRestricted]: (t) => t('paymentMethod:errors.unsupportedCardIssuerRestricted'),
  [TopazStatus.CashAppCardIneligible]: (t) => t('paymentMethod:errors.cashAppCardIneligibleTopaz'),
  [TopazStatus.FailedToRetrieveNewCard]: unknownErrorMessage,
  [TopazStatus.InvalidState]: unknownErrorMessage,
  [TopazStatus.ExceededMaxAttemptsEnteringDetails]: unknownErrorMessage,
  [TopazStatus.Error]: unknownErrorMessage,
}

const recoverableCardServiceErrorMessageSelectors: Record<RecoverableErrorCode, ErrorMessageSelector> = {
  [RecoverableErrorCode.UnsupportedCardIssuerBank]: (t) => t('paymentMethod:errors.unsupportedCardIssuerBank'),
  [RecoverableErrorCode.UnsupportedCardIssuer]: (t) => t('paymentMethod:errors.foreignCard'),
  [RecoverableErrorCode.UnsupportedCardType]: (t) => t('paymentMethod:errors.prepaidCard'),
  [RecoverableErrorCode.AddPaymentActiveCardLimitExceeded]: (t) => t('paymentMethod:errors.activeCardLimitExceeded'),
  [RecoverableErrorCode.AddPaymentInactiveCardLimitExceeded]: (t) => t('paymentMethod:errors.inactiveCardLimitExceeded'),
  [RecoverableErrorCode.CreditCardProhibitedForPaymentType]: (t) => t('paymentMethod:errors.creditCardNotAllowed'),
  [RecoverableErrorCode.CashAppCardIneligible]: (t) => t('paymentMethod:errors.cashAppCardIneligible'),
}

export const selectThreeDSErrorMessage = (t: TFunction, statusCode: number | undefined): string => {
  if (statusCode === undefined) {
    return unknownErrorMessage(t)
  }

  return (threeDSErrorMessageSelectors[statusCode as InitiateThreeDSErrorCode] ?? unknownErrorMessage)(t)
}

export const selectTopazErrorMessage = (t: TFunction, status: TopazStatus): string => {
  const selector = topazErrorMessageSelectors[status]

  return (selector ?? unknownErrorMessage)(t)
}

export const selectRecoverableCardServiceErrorMessage = (t: TFunction, errorCode: string | undefined): string => {
  if (errorCode === undefined) {
    return unknownErrorMessage(t)
  }

  return (recoverableCardServiceErrorMessageSelectors[errorCode as RecoverableErrorCode] ?? unknownErrorMessage)(t)
}
